import { Box, Stack } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import { HeadTag } from 'shared/components';
import LeftMenu from './components/LeftMenu';
import RightMenu from './components/RightMenu';

import { useUploadPageState } from 'shared/hooks/UploadPage';
import { fileValidation } from 'shared/helpers/FileValidation';
import UploadFile from '../upload-packet/UploadZone';
import ProgressZone from '../upload-packet/ProgressZone';
import { UploadPacketReworkService } from 'services';
import { Toast, Utils } from 'shared/helpers';
import { SignedReworkUrlTester } from 'shared/hooks/APITester';
import { AzureStorage } from 'shared/helpers/AzureStorage';
import { useNavigate, useParams } from 'react-router-dom';
import { formattingAction } from 'shared/helpers/FormatAction';
import { Formik, FormikProps } from 'formik';
import { UploadReworkZoneScheme } from 'validators/UploadReworkScheme';
import DocumentModal, { inferDocumentModal } from './components/DocumentModal';
import { AppPrimaryButton } from 'shared/components/AppComponents';
import { AxiosError } from 'axios';

const UploadReworkPage = (): ReactElement => {
    const documentModalRef = React.useRef<inferDocumentModal | null>(null);
    const formikRef = React.useRef<FormikProps<FormikPropsValues> | null>(null);
    const navigation = useNavigate();
    const { packetId, token } = useParams();
    const [binNumber, setBinNumber] = React.useState<number>();
    const [loanNumber, setLoanNumber] = React.useState<string>();
    const [surNameOfSolicitor, setSurName] = React.useState<string>('');
    const [, setUniqRef] = React.useState<string>('');
    const [status, setStatus] = React.useState<string>('');
    const [statusMessage, setStatusMessage] = React.useState<string>('');
    const [slaDate, setSLADate] = React.useState<string>('');
    const [documents, setDocuments] = React.useState<ReworkDocuments[]>();
    const [clientName, setClientName] = React.useState<string>('');
    const [packetType, setPacketType] = React.useState<string>('');
    const [createdAt, setCreatedDate] = React.useState<string>('');
    const [createdBy, setCreatedBy] = React.useState<string>('');
    const [, setEscalatedDate] = React.useState<string>('');
    const [, setCompletedDate] = React.useState<string>('');
    const [expiryDate, setExpiryDate] = React.useState<string>('');
    const [maxDocument, setMaxDocumentUpload] = React.useState<number | string>(
        'max'
    );
    const [filesUploaded, setFileUploaded] = React.useState<FilesUploaded>();
    const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
    const [state, setState] = useUploadPageState();

    /**
     * Ref for the file input element.
     * @type {React.RefObject<HTMLInputElement>}
     */
    const fileRef = React.useRef<HTMLInputElement>(null);
    /**
     * Handles the click event on the upload button.
     */
    const handleClick = (): void => {
        fileRef.current?.click();
        setState({ ...state, isChoosingFile: true });
    };

    React.useEffect(() => {
        if (packetId && token) {
            UploadPacketReworkService.getReworkPackets({ packetId, token })
                .then((response: { data: ReworkResponse }) => {
                    const {
                        binNumber: _binNumber,
                        loanNumber: _loanNumber,
                        solicitor,
                        uniqueRef,
                        status: _status,
                        statusMessage: _statusMessage,
                        slaDate: _slaDate,
                        documents: _documents,
                        clientName: _clientName,
                        reworkDetails,
                        type,
                        clientId,
                        createdAt: _createdAt,
                        docNeededCount,
                        operatorName,
                        escalatedDate: _escalatedDate,
                        completedDate: _completedDate,
                        expiryDate: _expiryDate,
                    } = response.data.data;
                    setBinNumber(_binNumber);
                    setLoanNumber(_loanNumber);
                    setSurName(solicitor.name);
                    setUniqRef(uniqueRef);
                    setStatus(_status);
                    setStatusMessage(_statusMessage);
                    setSLADate(_slaDate);
                    const ArrayDocumentsTask = Array.from(_documents)
                        .map((i) => ({
                            ...i,
                            isMissingDocument: false,
                        }))
                        .concat(
                            Array.from(
                                reworkDetails.expectedMissingDocuments
                            ).map((o) => ({
                                _id: '',
                                placeholder: o,
                                isMissingDocument: true,

                                file: '',
                                category: '',
                                statusMessage: '',
                                processedFile: '',
                                sasToken: '',
                                predefinedReWorkMessage: null,
                            }))
                        );
                    setDocuments(ArrayDocumentsTask);
                    setClientName(_clientName);
                    setPacketType(type);
                    setCreatedDate(_createdAt);
                    setEscalatedDate(_escalatedDate);
                    setCompletedDate(_completedDate);
                    setExpiryDate(_expiryDate);
                    const lastName =
                        typeof operatorName === 'object' &&
                        operatorName !== null
                            ? operatorName.last
                            : '';
                    const firstName =
                        typeof operatorName === 'object' &&
                        operatorName !== null
                            ? operatorName.first
                            : '';
                    const nameOperator = String(firstName)
                        .substring(0, 1)
                        .toUpperCase();

                    const operatorBy =
                        typeof operatorName === 'object' &&
                        operatorName !== null
                            ? `${lastName}, ${nameOperator}`
                            : '-';
                    setCreatedBy(operatorBy);
                    setMaxDocumentUpload(docNeededCount);
                    setState((prevState) => ({
                        ...prevState,
                        valueBin: String(_binNumber),
                        valueClient: {
                            ...prevState.valueClient,
                            name: _clientName,
                            actions: formattingAction(clientId),
                        },
                    }));
                })
                .catch((error) => {
                    navigation(`/expired-upload-rework/${packetId}/${token}`, {
                        state: {
                            msg: error.response.data
                                ? error.response.data.msg
                                : '',
                        },
                    });
                });
        }
    }, [navigation, packetId, setState, token]);

    /**
     * Handles the file upload event.
     *
     * @param {ChangeEvent<HTMLInputElement>} event - The file upload event.
     */
    const handleFileUpload = React.useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>): Promise<any> => {
            const { files } = event.target;
            if (formikRef.current) {
                const { getFieldHelpers, values } = formikRef.current;
                const fieldHelper = getFieldHelpers('attachment');
                if (files && files.length > 0) {
                    setState((prevState) => ({
                        ...prevState,
                        isProcessingData: true,
                    }));
                    const fileArray = Array.from<File>({
                        length: files.length,
                    }).map((i, index) => {
                        const fileName = files.item(index);
                        return fileName;
                    });
                    let blobData:
                        | {
                              blobdata: Uint8Array;
                              name: string;
                          }[]
                        | null = null;
                    const validation = await fileValidation(
                        event,
                        (data) => {
                            blobData = data;
                            setState((prevState) => ({
                                ...prevState,
                                isProcessingData: false,
                            }));
                        },
                        2 * 1024 * 1024 * 1024,
                        typeof maxDocument === 'number' ? maxDocument : 0,
                        'documents'
                    )
                        .then((bool) => bool)
                        .catch(() => false);
                    if (validation && blobData) {
                        const filesArray = Array.from<any>({
                            length: files.length,
                        }).map((i: any, index: number) => {
                            const fileName = files.item(index)?.name;
                            return {
                                name: fileName ?? '',
                            };
                        });
                        fieldHelper.setValue(
                            values.attachment.concat(filesArray)
                        );
                        setState((prevState) => ({
                            ...prevState,
                            fileBlob:
                                prevState.fileBlob && blobData
                                    ? [...prevState.fileBlob, ...blobData]
                                    : blobData,
                            fileData: [...prevState.fileData, ...fileArray],
                            validateFile: true,
                            isChoosingFile: false,
                            isProcessingData: false,
                        }));
                    }
                } else {
                    setState((prevState) => ({
                        ...prevState,
                        isChoosingFile: false,
                        isProcessingData: false,
                    }));
                }
            }
        },
        [maxDocument, setState]
    );

    /**
     * Handles the confirmation of the upload.
     */
    const confirmUpload = React.useCallback((): void => {
        setState({
            ...state,
            uploadProgress: true,
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Promise.all(
            state.fileBlob.map(async (item: { name: any }) => {
                try {
                    const { url, fileName } = await SignedReworkUrlTester(
                        item.name,
                        packetId ?? '',
                        token ?? ''
                    );
                    return {
                        ...item,
                        url,
                        fileName,
                        progress: 0,
                        networkerror: false,
                    };
                } catch (error) {
                    const networkerror = !!(
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    );

                    return {
                        ...item,
                        progress: -1,
                        networkerror,
                    };
                }
            })
        )
            .then((newBlob) => {
                setState((prevState) => ({
                    ...prevState,
                    fileBlob: newBlob,
                }));
                if (newBlob.some((item) => item.progress !== -1)) {
                    newBlob.forEach(async (item) => {
                        try {
                            if (item.progress !== -1) {
                                await AzureStorage(
                                    item.blobdata,
                                    item.url,
                                    (progress) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            fileBlob: prevState.fileBlob.map(
                                                (blobItem: { url: any }) =>
                                                    blobItem.url === item.url
                                                        ? {
                                                              ...blobItem,
                                                              progress,
                                                          }
                                                        : blobItem
                                            ),
                                        }));
                                    },
                                    (ev: string) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            fileStatus: ev,
                                        }));
                                    }
                                );
                            } else if (
                                item.progress === -1 &&
                                item.networkerror
                            ) {
                                throw new AxiosError('test', 'ERR_NETWORK');
                            }
                            // return;
                        } catch (error) {
                            // console.log(error);
                        }
                    });
                } else if (
                    newBlob.some((item) => item.progress !== -1) === false &&
                    newBlob.some(
                        (item) =>
                            item.progress === -1 && item.networkerror === false
                    ) === false
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                }
            })
            .catch(() => {
                // console.log(error);
            });
    }, [packetId, setState, state, token]);

    /**
     * Handles the removal of a file from the file data.
     *
     * @param {number} index - The index of the file to remove.
     */
    const handleRemoveFile = (index: number): void => {
        setState((prevState) => {
            const updatedFileData = [
                ...prevState.fileData.slice(0, index),
                ...prevState.fileData.slice(index + 1),
            ];
            const updatedFileBlob = [
                ...prevState.fileBlob.slice(0, index),
                ...prevState.fileBlob.slice(index + 1),
            ];
            if (updatedFileData.length < 1) {
                return {
                    ...prevState,
                    validateFile: false,
                    fileData: updatedFileData,
                    fileBlob: updatedFileBlob,
                };
            }
            return {
                ...prevState,
                fileData: updatedFileData,
                fileBlob: updatedFileBlob,
            };
        });
    };

    /**
     * Sends the uploaded files as a packet.
     *
     * @param {any[]} files - Array of files to be uploaded.
     * @returns {void}
     */
    const sendUpload = React.useCallback(
        (files: any[]): void => {
            if (!packetId) return;
            /**
             * Creates an array of files in a specific format.
             *
             * @type {any[]}
             */
            const filesBlob = files.map((item: any) => ({
                placeholder: item.name,
                file: item.fileName,
                status: item.progress === 100 ? 'successful' : 'unsuccessful',
            }));

            /**
             * Creates an new array
             *
             * @type {any[]}
             */
            const filesUpload = {
                packetId,

                clientId: state.valueClient.actions.uuid,
                // clientName: state.valueClient.name,
                // binNumber: String(binNumber),
                documents: Array.from(filesBlob).map((el) => ({
                    status: el.status,
                    placeholder: el.placeholder,
                    file: el.file,
                    comments: '',
                })),
            };
            setFileUploaded(filesUpload);
            setState({ ...state, openDialogComplete: true });
            Toast.showSuccessMessage('Uploaded successfully!');
            // const myFunction = async (): Promise<void> => {
            //     try {
            //         const response =

            //         if (response) {
            //             setState({ ...state, openDialogComplete: true });
            //         }
            //     } catch (error: any) {
            //         Toast.showErrorMessage(error.response.data.msg);
            //     }
            // };

            // myFunction();
        },
        [packetId, setState, state]
    );
    /**
     * generate classname uses to differentiate status in upload action
     */
    const classNameOnStatusUpload = useMemo((): string => {
        if (state.validateFile) {
            if (state.uploadProgress) {
                return 'UploadZone update uploading';
            }
            return 'UploadZone update';
        }
        return 'UploadZone dashboard';
    }, [state.validateFile, state.uploadProgress]);

    /**
     * generate classname uses to differentiate packet status
     */
    const renderOnLiteralToCreatePacketStatusClassName = (
        text: string
    ): string => {
        let className = '';
        switch (text) {
            case 'completed':
                className = 'Categorisation Completed';
                break;
            case 'verified':
                className = 'Verified';
                break;
            case 'escalated':
                className = 'Escalated';
                break;
            case 'review-required':
                className = 'Review Required';
                break;
            // case 'sentForRework':
            //     className = 'Sent for rework';
            //     break;
            case 'openbyanother':
                className = 'Open By another Member';
                break;
            case 'processingqy':
                className = 'Processing Verification';
                break;
            default:
                break;
        }
        return className;
    };

    /**
     * callback to handle submit in right menus
     */
    const handleSubmit = React.useCallback(async () => {
        if (filesUploaded && token) {
            setSubmitting(true);
            const { current: formik } = formikRef;
            const comments = formik ? formik.values.comments : [];
            const modifiedDocument = Array.from(filesUploaded.documents)
                .map((e, i) => ({
                    ...e,
                    comments:
                        comments.some((o, x) => x === i) === true
                            ? comments[i]
                            : '',
                }))
                .filter((e) => e.status === 'successful');
            await UploadPacketReworkService.uploadRework({
                ...filesUploaded,
                documents: modifiedDocument,
                token,
            })
                .then((_response) => {
                    setSubmitting(false);
                    Utils.handleSuccessResponse(_response);
                    navigation('/finish-upload-rework');
                })
                .catch((error) => {
                    setSubmitting(false);

                    if (
                        error !== null &&
                        error instanceof AxiosError &&
                        error.code === 'ERR_NETWORK'
                    ) {
                        Toast.showErrorMessage(
                            'No internet connection, Please try again!'
                        );
                    } else {
                        Utils.handleErrorResponse(error);
                    }
                });
        }
    }, [filesUploaded, navigation, token]);

    /**
     * fix failure uploading not reseted
     */
    React.useLayoutEffect(() => {
        if (!state.uploadProgress && state.failedUpload === true) {
            setState((state) => ({
                ...state,
                failedUpload: false,
            }));
        }
    }, [setState, state.uploadProgress]);
    const [modalDocumentFile, setModalDocumentFile] =
        React.useState<string>('');
    const [modalReason, setModalReason] = React.useState<string>('');
    const [modalDocumentPlaceholder, setModalDocumentPlaceholder] =
        React.useState<string>('');
    const [predefinedMsg, setPredefinedMessage] = React.useState<string | null>(
        null
    );
    const handleClickForViewDocument = React.useCallback(
        (index: number) => {
            const document = documents?.find((o, i) => i === index);
            if (document) {
                setModalDocumentFile(document.sasToken);
                setPredefinedMessage(document.predefinedReWorkMessage);
                setModalReason(document.statusMessage ?? '');
                setModalDocumentPlaceholder(document.placeholder ?? '');
                documentModalRef.current?.setOpen();
            }
        },
        [documents]
    );

    React.useEffect(() => {
        window.addEventListener('error', (e) => {
            if (
                e.message ===
                'ResizeObserver loop completed with undelivered notifications.'
            ) {
                const resizeObserverErrDiv = document.getElementById(
                    'webpack-dev-server-client-overlay-div'
                );
                const resizeObserverErr = document.getElementById(
                    'webpack-dev-server-client-overlay'
                );
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
        });
    }, []);

    return (
        <>
            <HeadTag title="Upload Cross Check records" />
            <Formik
                innerRef={formikRef}
                validationSchema={UploadReworkZoneScheme}
                initialValues={{
                    attachment: [],
                    comments: [],
                }}
                enableReinitialize
                onSubmit={() => {}}
            >
                <>
                    <input
                        type="file"
                        hidden
                        ref={fileRef}
                        accept=".pdf,.doc,.docx"
                        multiple
                        onInput={handleFileUpload}
                    />

                    <Stack direction="row" className="StackRework">
                        <LeftMenu
                            onClickViewDocument={handleClickForViewDocument}
                            solicitor={surNameOfSolicitor}
                            expirityDate={new Date(expiryDate || Date.now())}
                            documents={documents}
                        />
                        <Box mt={3} className={classNameOnStatusUpload}>
                            {!state.uploadProgress && (
                                <UploadFile
                                    maxFileRework={
                                        typeof maxDocument === 'number'
                                            ? maxDocument
                                            : 0
                                    }
                                    isRework
                                    canUpload={
                                        typeof state.valueClient === 'object' &&
                                        state.valueClient.actions.uuid &&
                                        ((typeof maxDocument === 'number' &&
                                            maxDocument > 0) ||
                                            maxDocument === 'max')
                                    }
                                    handleClick={handleClick}
                                    state={state}
                                    confirmUpload={confirmUpload}
                                    setFileData={(e: number) =>
                                        handleRemoveFile(e)
                                    }
                                    fileRef={fileRef}
                                    callback={(data: any) => {
                                        setState((prevState) => ({
                                            ...prevState,
                                            fileBlob: data,
                                        }));
                                    }}
                                    updating={(data: any) => {
                                        if (formikRef.current) {
                                            const { getFieldHelpers } =
                                                formikRef.current;
                                            const fieldHelper =
                                                getFieldHelpers('attachment');

                                            fieldHelper.setValue(
                                                Array.from(data).map(
                                                    (i: any) => ({
                                                        name: i.name,
                                                    })
                                                )
                                            );
                                            setState((prevState) => ({
                                                ...prevState,
                                                fileData: data,
                                                validateFile: true,
                                            }));
                                        }
                                    }}
                                />
                            )}
                            {state.uploadProgress && (
                                <ProgressZone
                                    fileCompleted={(files: any[]) => {
                                        sendUpload(files);
                                    }}
                                    state={state}
                                    setState={setState}
                                    isRework
                                    isReinitialize={false}
                                    // Uncomment this if you want to manually upload
                                    // setUploadZone={(e: boolean) => setState({ ...state, uploadZone: e })}
                                    // setUploadProgress={(e: boolean) => setState({ ...state, uploadProgress: e })}
                                />
                            )}
                            <Box className="menu-footer">
                                <AppPrimaryButton
                                    title="Save & Exit"
                                    isLoading={isSubmitting}
                                    loadingSize={22}
                                    loadingText="PROCESSING"
                                    className="btn-small download"
                                    onClick={handleSubmit}
                                    fullWidth
                                    disabled={
                                        !state.openDialogComplete ||
                                        isSubmitting
                                    }
                                />
                            </Box>
                        </Box>
                        {false && (
                            <RightMenu
                                packetStatus={renderOnLiteralToCreatePacketStatusClassName(
                                    status
                                )}
                                isLoading={isSubmitting}
                                actionOnSubmit={handleSubmit}
                                packetType={packetType}
                                SLADate={new Date(slaDate || Date.now())}
                                clientName={clientName}
                                bin={String(binNumber ?? '')}
                                loanNumber={loanNumber ?? ''}
                                uploadedDate={new Date(createdAt || Date.now())}
                                operatorId={createdBy}
                                reason={statusMessage}
                                confirmUploaded={state.openDialogComplete}
                            />
                        )}
                    </Stack>
                    <DocumentModal
                        documentFile={modalDocumentFile}
                        documentPlaceholder={modalDocumentPlaceholder}
                        documentReason={modalReason}
                        documentMessage={predefinedMsg}
                        ref={documentModalRef}
                    />
                </>
            </Formik>
        </>
    );
};

export default UploadReworkPage;
